import React, {useEffect, useMemo, useState} from "react";
import {IssueFiltersPaginatedQuery, Publisher, SeriesType, useGetIssuesViaElasticSearchQuery} from "@mwServices/Issue";
import usePreferences from "@hooks/usePreferences";
import SearchIssuesFiltersDrawer from "./SearchIssuesFiltersDrawer";
import IssuesResults from "@mwPages/issues/IssuesResults";
import {formatNumber} from "@lib/format";

// This defines the expected types for the params of the component.
interface SearchPageIssuesProps {
    query: string;
    isSearchBarcode: boolean;
    setQuery: (search: string) => void;
}

const SearchPageIssues = ({query, setQuery, isSearchBarcode}: SearchPageIssuesProps) => {
    const {  itemCount } = usePreferences();
    const [selectedComicIds, setSelectedComicIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');

    const defaultFilters = useMemo(() => ({
        showVariants: isSearchBarcode ? true : true,
        publishers: undefined,
        multipleDateRanges: undefined,
        formats: undefined,
        query: !isSearchBarcode ? query : "",
        subquery: undefined,
        barcode: isSearchBarcode ? query : undefined,
        sort: 0,
    }), [query, isSearchBarcode]);

    const [issueParams, setIssueParams] = useState<IssueFiltersPaginatedQuery>({
        filters: {...defaultFilters},
        page: 1,
        perPage: itemCount,
    });

    useEffect(() => {
        if(search !== issueParams.filters.subquery){
            setIssueParams({ ...issueParams, page: 1, filters: { ...issueParams.filters, subquery: search ? search : undefined } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        setSearch('');
        setIssueParams({
            filters: { ...defaultFilters },
            page: 1,
            perPage: itemCount,
        });
    }, [defaultFilters]);

    const issueQuery = useGetIssuesViaElasticSearchQuery(issueParams);

    const {issueData, pagination}  = useMemo(() => {
        if(issueQuery.data) return {
            issueData: issueQuery.data,
            pagination: issueQuery.data.pagination,
        };
        return { issueData: null, pagination: null };
    }, [issueQuery]);

    // Piece together the publisher and series types for filters. Maybe move into filters.
    const [publishers, setPublishers] = useState(new Array<Publisher>());
    const [seriesTypes, setSeriesTypes] = useState(new Array<SeriesType>());
    useEffect(() => {
        if (publishers.length == 0 && issueQuery && issueQuery.data && issueQuery.data.overview &&  issueQuery.data.overview.publishers) {
            let newPublishers = new Array<Publisher>();
            issueQuery.data.overview.publishers.forEach(publisher => {
                let keyArray = publisher.key.split('|');
                newPublishers.push({ id: Number(keyArray[0]), title: keyArray[1], count: publisher.doc_count});
            });
            setPublishers(newPublishers);
        }

        // If we don't already have series types, push those into the array
        if (seriesTypes.length == 0 && issueQuery && issueQuery.data && issueQuery.data.overview &&  issueQuery.data.overview.series_types) {
            let newSeriesTypes = new Array<SeriesType>();
            issueQuery.data.overview.series_types.forEach(seriesType => {
                newSeriesTypes.push({ title: seriesType.key, count: seriesType.doc_count});
            });
            setSeriesTypes(newSeriesTypes);
        }
    }, [issueQuery]);

    return (
        <IssuesResults
            issueQuery={issueQuery}
            issuesTitle={isSearchBarcode ?
                <span/> :
                <span>{pagination && formatNumber(pagination.total_items)} Issues</span>
            }
            infoModalData={null}
            defaultFilters={defaultFilters}
            issueParams={issueParams}
            setIssueParams={setIssueParams}
            search={search}
            setSearch={setSearch}
            query={query}
            selectedComicIds={selectedComicIds}
            setSelectedComicIds={setSelectedComicIds}
            filterDrawer={
                <SearchIssuesFiltersDrawer
                    filters={issueParams.filters}
                    setFilters={(filters) => setIssueParams({...issueParams, page: 1, filters})}
                    publishers={publishers}
                    seriesTypes={seriesTypes}
                    isHotCount={issueQuery.data ? issueQuery.data.overview.is_hot : null}
                    isKeyCount={issueQuery.data ? issueQuery.data.overview.is_key : null}
                    isRareCount={issueQuery.data ? issueQuery.data.overview.is_rare : null}
                />
            }
            isSearchBarcode={isSearchBarcode}
            isSearchES={true}
            useGridView={false}
            useListView={true}
            useListViewNoImages={false}
            showIssueNotes={false}
        />
    );
};

export default SearchPageIssues;